import { ApolloCache, Reference } from '@apollo/client';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import DateStamp from '@components/dates/DateStamp';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import TimeLogForm, { Journey } from '@components/forms/TimeLogForm';
import { LoadMoreArgs, PageInfo } from '@components/InfiniteScroll';
import Modal from '@components/Modal';
import Table, { TableHeader } from '@components/Table';
import LargeText from '@components/text/LargeText';
import TimeLogActions from '@components/TimeLogActions';
import styles from '@components/TimeLogTables.module.css';
import Hyperlink from '@components/typography/Hyperlink';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import {
  SortDirectionEnum,
  TimeLogFragment as TimeLog,
  TimeLogDestroyDocument,
  TimeLogDestroyMutation,
  TimeLogDestroyMutationVariables
} from '@typing/Generated';
import { journeyRoute } from '@utils/routeUtils';
import { sortByDate } from '@utils/timeLogUtils';
import { minutesToHoursAndMinutes } from '@utils/timeUtils';

export type DestroyCache = ApolloCache<TimeLogDestroyMutation>;

type Props = {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  fetchMore?: (args: LoadMoreArgs) => Promise<any>;
  journey?: Journey;
  pageInfo: PageInfo | undefined;
  refetch: () => void;
  skipScroll?: boolean;
  timeLogs: TimeLog[] | null;
};

const TimeLogTables = ({ fetchMore, journey, pageInfo, refetch, skipScroll = false, timeLogs }: Props) => {
  const intl = useIntl();
  const deleteOpener = useOpener();
  const [deletingTimeLog, setDeletingTimeLog] = useState<TimeLog | undefined>(undefined);
  const myExpert = useMyExpert();

  const modalOpener = useOpener();
  const [editingTimeLog, setEditingTimeLog] = useState<TimeLog | undefined>(undefined);

  const [destroyTimeLog] = useMutationWithValidation<TimeLogDestroyMutation, TimeLogDestroyMutationVariables>(
    TimeLogDestroyDocument,
    'timeLog',
    {
      update: (cache, { data }) => {
        if (data?.timeLogDestroy?.id) {
          cache.modify({
            fields: {
              timeLogs(cachedTimeLogRefs: readonly Reference[], { readField }) {
                return cachedTimeLogRefs.filter(timeLogRef => data.timeLogDestroy?.id !== readField('id', timeLogRef));
              }
            },
            id: cache.identify({ __typename: 'Journey', id: deletingTimeLog?.journey?.id })
          });
        }
        refetch();
      }
    }
  );

  const handleDelete = useCallback(() => {
    if (deletingTimeLog) {
      destroyTimeLog({ id: deletingTimeLog.id }).then(() => {
        deleteOpener.close();
        setDeletingTimeLog(undefined);
      });
    }
  }, [deleteOpener, deletingTimeLog, destroyTimeLog]);

  const handleDeleteClick = useCallback(
    (timeLog: TimeLog) => {
      setDeletingTimeLog(timeLog);
      deleteOpener.open();
    },
    [deleteOpener]
  );

  const handleEditClick = useCallback(
    (timeLog: TimeLog) => {
      setEditingTimeLog(timeLog);
      modalOpener.open();
    },
    [modalOpener]
  );

  const generateJourneyLink = useCallback(
    (timeLog: TimeLog) => {
      if (myExpert) {
        return journeyRoute({ journeyId: timeLog.journey?.id ?? '', linkAfterJourney: '/care-plan', myExpert });
      }
      return `/admin/clients/${timeLog.journey?.owner.id ?? ''}`;
    },
    [myExpert]
  );

  const tableHeaders: TableHeader<SortDirectionEnum>[] = [
    { messageKey: 'dictionary.date' },
    { messageKey: 'dictionary.time' },
    { messageKey: 'dictionary.actor' },
    { messageKey: 'dictionary.journey' },
    { messageKey: 'dictionary.activity' },
    { messageKey: 'dictionary.support' },
    { messageKey: 'dictionary.notes' },
    { messageKey: 'dictionary.carePlanning' },
    { messageKey: 'dictionary.actions' }
  ];

  return (
    <>
      {timeLogs ? (
        <Table<SortDirectionEnum>
          fetchMore={fetchMore}
          headers={tableHeaders}
          pageInfo={pageInfo}
          skipScroll={skipScroll}
          skipSpacing={skipScroll}
        >
          {sortByDate(timeLogs).map(timeLog => (
            <tr key={timeLog.id}>
              <td>
                <DateStamp absoluteDate showWeekday={false} value={timeLog.date} />
              </td>
              <td>{minutesToHoursAndMinutes({ minutes: timeLog.minutes })}</td>
              <td>{timeLog.actorName}</td>
              <td>
                <Hyperlink routerLink={generateJourneyLink(timeLog)}>{timeLog.journey?.name}</Hyperlink>
              </td>
              <td>
                <FormattedMessage id={`models.timeLogActivityKinds.${timeLog.activityKind.billable ? 'b' : 'nb'}`} />
                <FormattedMessage id={`models.timeLogActivityKinds.${timeLog.activityKind.kind}.title`} />
              </td>
              <td>
                {timeLog.supportKind?.kind && (
                  <FormattedMessage id={`models.timeLogSupportKinds.${timeLog.supportKind?.kind}`} />
                )}
              </td>
              <td>{timeLog.notes}</td>
              <td>
                {timeLog.isCarePlanning ? (
                  <FormattedMessage id="dictionary.yes" />
                ) : (
                  <FormattedMessage id="dictionary.no" />
                )}
              </td>
              <td>
                <TimeLogActions onDelete={handleDeleteClick} onEdit={handleEditClick} timeLog={timeLog} />
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <LargeText>
          <FormattedMessage id="dictionary.noTimeLogsFound" />
        </LargeText>
      )}

      <Modal
        cssClass="fullscreen"
        opener={modalOpener}
        title={intl.formatMessage({ id: 'models.timeLog.editTimeLog' })}
      >
        {({ modalDisplayed }) => (
          <>
            <TimeLogForm
              autofocus={modalDisplayed}
              journey={journey}
              onUpdate={modalOpener.close}
              refetch={refetch}
              timeLog={editingTimeLog}
            />
            <div className={styles.spacing} />
          </>
        )}
      </Modal>
      <DeleteConfirmationAlert messageKey="models.timeLog.reallyDelete" onDelete={handleDelete} opener={deleteOpener} />
    </>
  );
};

export default TimeLogTables;
